import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";
import { Eye, EyeOff, AlertTriangle, X, EllipsisVertical } from "lucide-react-native";
import { styles, colors, shadows } from "../styles";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[UI ErrorBoundary]", error, info);
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <View style={styles.page}>
          <Card>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
              <AlertTriangle size={20} color={colors.danger} />
              <Text style={styles.h2}>System Encountered an Error</Text>
            </View>
            <Text style={[styles.message, styles.errorMessage]}>
              {this.state.error.message || "An unexpected error occurred in this view. Please try refreshing or navigating back."}
            </Text>
          </Card>
        </View>
      );
    }
    return this.props.children;
  }
}

export function YNSLogo({ width = 170, height = 40, style }) {
  let logoSrc = "";
  try {
    const asset = require("../assets/logo.svg");
    logoSrc = typeof asset === "object" && asset.default ? asset.default : asset;
  } catch (e) {
    logoSrc = "/logo.svg";
  }

  return (
    <img
      src={logoSrc || "/logo.svg"}
      alt="YNS Logo"
      style={{
        width: width,
        height: height,
        objectFit: "contain",
        display: "block",
        ...style
      }}
      onError={(e) => {
        if (e.target.src !== "/logo.svg") {
          e.target.src = "/logo.svg";
        }
      }}
    />
  );
}

export function Button({ children, onPress, variant = "primary", icon: Icon, disabled, loading, style }) {
  const [hovered, setHovered] = useState(false);
  const isSecondary = variant === "secondary";
  const isDanger = variant === "danger";

  let bg = colors.primary;
  let textColor = "#FFFFFF";
  let borderColor = "transparent";

  if (isSecondary) {
    bg = hovered ? colors.secondaryHover : colors.secondaryBg;
    textColor = colors.text;
    borderColor = colors.border;
  } else if (isDanger) {
    bg = hovered ? colors.dangerHover : colors.danger;
    textColor = "#FFFFFF";
  } else {
    bg = hovered ? colors.primaryHover : colors.primary;
  }

  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled || loading}
      onPress={disabled || loading ? undefined : onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.button,
        {
          backgroundColor: bg,
          borderColor,
          borderWidth: isSecondary ? 1 : 0,
          opacity: disabled ? 0.55 : 1,
          flexShrink: 0
        },
        style
      ]}
    >
      {loading ? (
        <Text style={[styles.buttonText, { color: textColor }]}>Processing...</Text>
      ) : (
        <>
          {Icon ? <Icon size={16} color={textColor} style={{ flexShrink: 0 }} /> : null}
          <Text style={[styles.buttonText, { color: textColor }]}>{children}</Text>
        </>
      )}
    </Pressable>
  );
}

export function IconButton({
  icon: Icon,
  onPress,
  title,
  variant = "secondary",
  disabled,
  style,
  size = 32,
  iconSize = 16
}) {
  const [hovered, setHovered] = useState(false);

  let bg = colors.secondaryBg;
  let iconColor = colors.text;
  let borderColor = colors.border;

  if (variant === "primary") {
    bg = hovered ? colors.primaryHover : colors.primary;
    iconColor = "#FFFFFF";
    borderColor = "transparent";
  } else if (variant === "danger") {
    bg = hovered ? colors.dangerHover : "#FEE2E2";
    iconColor = hovered ? "#FFFFFF" : colors.danger;
    borderColor = hovered ? colors.dangerHover : "#FECACA";
  } else if (variant === "success") {
    bg = hovered ? "#059669" : "#D1FAE5";
    iconColor = hovered ? "#FFFFFF" : "#047857";
    borderColor = hovered ? "#059669" : "#A7F3D0";
  } else if (variant === "warning") {
    bg = hovered ? "#D97706" : "#FEF3C7";
    iconColor = hovered ? "#FFFFFF" : "#B45309";
    borderColor = hovered ? "#D97706" : "#FDE68A";
  } else if (variant === "info") {
    bg = hovered ? "#2563EB" : "#DBEAFE";
    iconColor = hovered ? "#FFFFFF" : "#1D4ED8";
    borderColor = hovered ? "#2563EB" : "#BFDBFE";
  } else {
    // secondary
    bg = hovered ? colors.secondaryHover : colors.surface;
    iconColor = hovered ? colors.text : colors.muted;
    borderColor = hovered ? colors.borderDark : colors.border;
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      title={title}
      disabled={disabled}
      onPress={disabled ? undefined : onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        {
          width: size,
          height: size,
          borderRadius: 6,
          backgroundColor: bg,
          borderWidth: 1,
          borderColor,
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.45 : 1,
          flexShrink: 0,
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
        },
        style
      ]}
    >
      {Icon ? <Icon size={iconSize} color={iconColor} /> : null}
    </Pressable>
  );
}

let activeActionMenuCloser = null;

export function ActionMenu({ actions = [] }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const visibleActions = actions.filter(Boolean);

  const closeSelf = React.useCallback(() => {
    setOpen(false);
    if (activeActionMenuCloser === closeSelf) {
      activeActionMenuCloser = null;
    }
  }, []);

  const updatePosition = React.useCallback(() => {
    if (!btnRef.current || typeof window === "undefined") return;
    const node = btnRef.current;
    if (typeof node.getBoundingClientRect !== "function") return;
    const rect = node.getBoundingClientRect();

    const menuHeight = visibleActions.length * 38 + 12;
    const menuWidth = 190;
    const padding = 8;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top;
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      // Not enough room below, open above the button
      top = Math.max(padding, rect.top - menuHeight - 4);
    } else {
      // Room below, open beneath the button
      top = Math.min(window.innerHeight - menuHeight - padding, rect.bottom + 4);
    }

    let right = window.innerWidth - rect.right;
    if (right < padding) right = padding;
    if (right + menuWidth > window.innerWidth - padding) {
      right = Math.max(padding, window.innerWidth - menuWidth - padding);
    }

    setCoords({ top, right });
  }, [visibleActions.length]);

  const toggle = (e) => {
    e?.stopPropagation?.();
    if (open) {
      closeSelf();
    } else {
      // Immediately close any other open ActionMenu in the entire app!
      if (activeActionMenuCloser && activeActionMenuCloser !== closeSelf) {
        activeActionMenuCloser();
      }
      activeActionMenuCloser = closeSelf;
      updatePosition();
      setOpen(true);
    }
  };

  // Listen for outside clicks, scrolling, resize, and Escape
  React.useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e) => {
      if (btnRef.current && btnRef.current.contains && btnRef.current.contains(e.target)) {
        return;
      }
      if (menuRef.current && menuRef.current.contains && menuRef.current.contains(e.target)) {
        return;
      }
      closeSelf();
    };

    const handleScrollOrResize = () => {
      closeSelf();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeSelf();
    };

    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize, true);
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize, true);
      window.removeEventListener("keydown", handleKeyDown, true);
      if (activeActionMenuCloser === closeSelf) {
        activeActionMenuCloser = null;
      }
    };
  }, [open, closeSelf]);

  if (visibleActions.length === 0) return null;

  const menuDropdown = open && typeof document !== "undefined" ? (
    <>
      {/* Invisible backdrop to capture outside clicks immediately */}
      <div
        onPointerDown={(e) => {
          e.stopPropagation();
          closeSelf();
        }}
        onClick={(e) => {
          e.stopPropagation();
          closeSelf();
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999998,
          backgroundColor: "transparent"
        }}
      />

      {/* Floating Menu Popover directly in document.body */}
      <div
        ref={menuRef}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: coords.top,
          right: coords.right,
          zIndex: 999999,
          backgroundColor: "#FFFFFF",
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.14), 0 3px 8px rgba(0,0,0,0.06)",
          minWidth: "185px",
          padding: "4px 0",
          display: "flex",
          flexDirection: "column",
          userSelect: "none"
        }}
      >
        {visibleActions.map((act, i) => {
          const Icon = act.icon;
          const isDanger = act.variant === "danger";
          const isPrimary = act.variant === "primary";
          const isSuccess = act.variant === "success";
          return (
            <div
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                closeSelf();
                act.onPress?.();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "9px 14px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: isPrimary || isSuccess ? "600" : "500",
                color: isDanger ? colors.danger : isPrimary || isSuccess ? colors.primary : colors.text,
                borderBottom: act.divider ? `1px solid ${colors.border}` : "none",
                backgroundColor: "transparent",
                transition: "background-color 0.12s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDanger ? "#FEE2E2" : colors.secondaryBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {Icon ? (
                <Icon size={15} color={isDanger ? colors.danger : isPrimary || isSuccess ? colors.primary : colors.muted} />
              ) : null}
              <span>{act.label}</span>
            </div>
          );
        })}
      </div>
    </>
  ) : null;

  return (
    <View ref={btnRef} style={{ position: "relative" }}>
      <Pressable
        onPress={toggle}
        accessibilityRole="button"
        accessibilityLabel="Action options menu"
        title="More Actions"
        style={({ hovered }) => [
          {
            width: 32,
            height: 32,
            borderRadius: 6,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: open || hovered ? colors.secondaryHover : colors.secondaryBg,
            borderWidth: 1,
            borderColor: open || hovered ? colors.borderDark : colors.border,
            cursor: "pointer"
          }
        ]}
      >
        <EllipsisVertical size={16} color={colors.text} />
      </Pressable>

      {open && typeof document !== "undefined"
        ? ReactDOM.createPortal(menuDropdown, document.body)
        : null}
    </View>
  );
}

export function Field({
  label,
  value,
  onChangeText,
  placeholder,
  multiline,
  keyboardType,
  style,
  secureTextEntry,
  editable = true,
  icon: Icon,
  error
}) {
  const [focused, setFocused] = useState(false);

  return (
    <View style={[{ flex: 1, minWidth: 160 }, style]}>
      {label ? <Text style={styles.label} numberOfLines={1}>{label}</Text> : null}
      <View
        style={{
          flexDirection: "row",
          alignItems: multiline ? "flex-start" : "center",
          borderWidth: 1,
          borderColor: error ? colors.danger : focused ? colors.primary : colors.border,
          borderRadius: 8,
          backgroundColor: editable ? colors.surface : colors.secondaryBg,
          paddingHorizontal: 12,
          height: multiline ? "auto" : 38
        }}
      >
        {Icon ? <Icon size={16} color={colors.muted} style={{ marginRight: 8, marginTop: multiline ? 10 : 0 }} /> : null}
        <TextInput
          value={value == null ? "" : String(value)}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.subtle}
          multiline={multiline}
          keyboardType={keyboardType}
          secureTextEntry={secureTextEntry}
          editable={editable}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={[
            {
              flex: 1,
              fontSize: 13,
              color: colors.text,
              paddingVertical: multiline ? 8 : 4,
              minHeight: multiline ? 75 : 34,
              outlineStyle: "none"
            }
          ]}
        />
      </View>
      {error ? <Text style={[styles.small, { color: colors.danger, marginTop: 2 }]}>{error}</Text> : null}
    </View>
  );
}

export function PasswordInput({ label = "Password", value, onChangeText, placeholder, error, style }) {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <View style={[{ flex: 1, minWidth: 160 }, style]}>
      {label ? <Text style={styles.label} numberOfLines={1}>{label}</Text> : null}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderWidth: 1,
          borderColor: error ? colors.danger : focused ? colors.primary : colors.border,
          borderRadius: 8,
          backgroundColor: colors.surface,
          paddingHorizontal: 12,
          height: 38
        }}
      >
        <TextInput
          value={value == null ? "" : String(value)}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.subtle}
          secureTextEntry={!showPassword}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            fontSize: 13,
            color: colors.text,
            height: 34,
            outlineStyle: "none"
          }}
        />
        <Pressable onPress={() => setShowPassword(!showPassword)} style={{ padding: 4 }}>
          {showPassword ? <EyeOff size={16} color={colors.muted} /> : <Eye size={16} color={colors.muted} />}
        </Pressable>
      </View>
      {error ? <Text style={[styles.small, { color: colors.danger, marginTop: 2 }]}>{error}</Text> : null}
    </View>
  );
}

export function Select({ label, value, onChangeText, children, style, disabled }) {
  return (
    <View style={[{ flex: 1, minWidth: 160 }, style]}>
      {label ? <Text style={styles.label} numberOfLines={1}>{label}</Text> : null}
      <select
        value={value || ""}
        disabled={disabled}
        onChange={(event) => onChangeText && onChangeText(event.target.value)}
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
          padding: "0 12px",
          minWidth: "140px",
          backgroundColor: disabled ? "#f1f5f9" : colors.surface,
          height: "38px",
          fontSize: "13px",
          color: disabled ? "#64748b" : colors.text,
          outline: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          width: "100%",
          boxSizing: "border-box",
          opacity: disabled ? 0.75 : 1
        }}
      >
        {children}
      </select>
    </View>
  );
}

export function Card({ children, style }) {
  return <View style={[styles.card, style]}>{children}</View>;
}

export function Badge({ children, variant = "primary" }) {
  let bg = colors.primaryLight;
  let text = colors.primary;

  if (variant === "success" || variant === "approved") {
    bg = colors.primaryLight;
    text = colors.primary;
  } else if (variant === "danger" || variant === "rejected") {
    bg = colors.dangerLight;
    text = colors.danger;
  } else if (variant === "warning" || variant === "pending" || variant === "draft") {
    bg = colors.warningLight;
    text = colors.warning;
  } else if (variant === "secondary" || variant === "info") {
    bg = colors.infoLight;
    text = colors.info;
  }

  return (
    <View style={[styles.badge, { backgroundColor: bg }]}>
      <Text style={{ color: text, fontSize: 11, fontWeight: "600" }}>{children}</Text>
    </View>
  );
}

export function DataTable({ columns, rows, renderActions, actionsWidth = 56 }) {
  return (
    <View style={[styles.card, { padding: 0, overflow: "visible", width: "100%" }]}>
      <View style={{ width: "100%" }}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          {columns.map((col) => {
            const colFlex = col.flex != null ? col.flex : 1;
            return (
              <View
                key={col.key}
                style={[
                  {
                    flex: colFlex,
                    minWidth: 0,
                    paddingHorizontal: 8,
                    justifyContent: "center"
                  },
                  col.style
                ]}
              >
                <Text style={styles.cellHeader} numberOfLines={1}>
                  {col.label}
                </Text>
              </View>
            );
          })}
          {renderActions ? (
            <View
              style={{
                width: actionsWidth,
                paddingHorizontal: 6,
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <Text style={styles.cellHeader}>Actions</Text>
            </View>
          ) : null}
        </View>

        {/* Table Rows */}
        {rows.map((row, idx) => (
          <View
            key={row.id || idx}
            style={[
              styles.tableRow,
              idx % 2 === 1 && styles.tableRowAlt,
              idx === rows.length - 1 && { borderBottomWidth: 0 }
            ]}
          >
            {columns.map((col) => {
              const colFlex = col.flex != null ? col.flex : 1;
              const content = col.render ? col.render(row) : row[col.key];
              return (
                <View
                  key={col.key}
                  style={[
                    {
                      flex: colFlex,
                      minWidth: 0,
                      paddingHorizontal: 8,
                      justifyContent: "center"
                    },
                    col.style
                  ]}
                >
                  {typeof content === "string" || typeof content === "number" ? (
                    <Text style={[styles.cell, col.style]} numberOfLines={1}>{content}</Text>
                  ) : content != null ? (
                    content
                  ) : (
                    <Text style={[styles.cell, { color: colors.subtle }]}>-</Text>
                  )}
                </View>
              );
            })}
            {renderActions ? (
              <View
                style={{
                  width: actionsWidth,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 6,
                  flexShrink: 0
                }}
              >
                {renderActions(row)}
              </View>
            ) : null}
          </View>
        ))}

        {rows.length === 0 ? (
          <View style={{ padding: 28, alignItems: "center", justifyContent: "center" }}>
            <Text style={[styles.small, { fontSize: 13 }]}>No records found.</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

export function ScreenHeader({ title, subtitle, children }) {
  return (
    <View style={[styles.between, { flexWrap: "wrap", gap: 12, marginBottom: 4 }]}>
      <View style={{ flex: 1, minWidth: 220 }}>
        <Text style={styles.title}>{title}</Text>
        {subtitle ? <Text style={[styles.small, { marginTop: 2, fontSize: 13 }]}>{subtitle}</Text> : null}
      </View>
      {children ? (
        <View style={{ flexDirection: "row", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {children}
        </View>
      ) : null}
    </View>
  );
}

export function StatCard({ label, value, icon: Icon, subtitle, style }) {
  return (
    <View style={[styles.stat, style]}>
      <View style={[styles.between, { marginBottom: 4 }]}>
        <Text style={[styles.small, { fontWeight: "600", fontSize: 12, flex: 1 }]} numberOfLines={1}>{label}</Text>
        {Icon ? <Icon size={18} color={colors.primary} style={{ marginLeft: 6, flexShrink: 0 }} /> : null}
      </View>
      <Text style={styles.statNumber} numberOfLines={1}>{value}</Text>
      {subtitle ? <Text style={[styles.small, { fontSize: 11, marginTop: 2 }]} numberOfLines={1}>{subtitle}</Text> : null}
    </View>
  );
}

export function Toolbar({ children, style }) {
  return (
    <View
      style={[
        {
          flexDirection: "row",
          backgroundColor: colors.surface,
          padding: 12,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: colors.border,
          gap: 10,
          flexWrap: "wrap",
          alignItems: "flex-end"
        },
        style
      ]}
    >
      {children}
    </View>
  );
}

export function PageContainer({ children }) {
  return <View style={styles.page}>{children}</View>;
}

export function Modal({ visible, title, onClose, children, maxWidth = 760 }) {
  if (!visible) return null;
  return (
    <View
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(17, 24, 39, 0.6)",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        padding: 16
      }}
    >
      <View
        style={[
          styles.card,
          {
            width: "100%",
            maxWidth: maxWidth,
            maxHeight: "92vh",
            overflow: "hidden",
            ...shadows.dropdown
          }
        ]}
      >
        <View style={[styles.between, { paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: colors.border, marginBottom: 8 }]}>
          <Text style={[styles.h2, { flex: 1, marginRight: 12 }]} numberOfLines={1}>{title}</Text>
          <Pressable onPress={onClose} style={{ padding: 6, borderRadius: 6, backgroundColor: colors.secondaryBg }}>
            <X size={18} color={colors.text} />
          </Pressable>
        </View>
        <ScrollView style={{ flexGrow: 0 }} contentContainerStyle={{ paddingVertical: 4 }}>
          {children}
        </ScrollView>
      </View>
    </View>
  );
}
